import { EditorState } from "draft-js";
import moment from "moment-timezone";
import { set as dotPropSet } from "dot-prop-immutable";
import {
  INITIALIZE_FORM,
  UPDATE_FORM,
  UPDATE_FORM_AT,
  VALIDATE_FORM_SUCCEEDED,
  VALIDATE_FORM_FAILED,
} from "editor/actions/form";
import {
  APPEND_NEWSLETTER_CHUNK,
  UPDATE_NEWSLETTER_CHUNK,
  MOVE_NEWSLETTER_CHUNK_UP,
  MOVE_NEWSLETTER_CHUNK_DOWN,
  DELETE_NEWSLETTER_CHUNK,
  GET_NEWSLETTER_SUCCEEDED,
} from "editor/actions/newsletter";

import { editorDecorators } from "editor/decorators";
import { TEMPLATES, RUBRICS } from "config/constants/editor";
import { STATUS } from "config/constants";

export const defaultCommonState = {
  errors: {},
};

export const storyInitialState = {
  headline: "",
  slug: "",
  content: EditorState.createEmpty(editorDecorators),
  id: null,
  authors: [],
  editors: [],
  sections: [],
  featured_sections: [],
  topics: [],
  subtopics: [],
  featured_subtopics: [],
  featured_date: null,
  embargo_until: null,
  tags: [],
  og_headline: "",
  og_description: "",
  seo_headline: "",
  primary_section: null,
  primary_tag: null,
  isTopStory: false,
  include_in_latest_stories: true,
  publish_to_apple_news: true,
  needs_visuals: false,
  rubric: RUBRICS.DEFAULT,
  template: TEMPLATES.DEFAULT,
  primaryImage: null,
  socialImage: null,
  alert_subject: "",
  alert_text: "",
  alert_list: [],
  status: STATUS.UNASSIGNED.slug,
  errors: {},
  is_breaking_news: false,
  slug_is_user_modified: false,
};

export const newsletterInitialState = {
  intro: EditorState.createEmpty(editorDecorators),
  outro: EditorState.createEmpty(editorDecorators),
  subject_line: "",
  preview_text: "",
  push_notification_text: "",
  subscription: {},
  id: null,
  chunks: [],
  publish_to_apple_news: true,
  needs_visuals: false,
  status: STATUS.UNASSIGNED.slug,
  errors: {},
};

const newsletterChunkInitialState = {
  id: null,
  blocks: EditorState.createEmpty(editorDecorators),
  headline: "",
  primary_image: null,
  is_expert_voices: false,
};

export const initialState = Object.assign(
  storyInitialState,
  newsletterInitialState
);

export default function(state = initialState, action) {
  switch (action.type) {
    case INITIALIZE_FORM: {
      const embargo_until = moment(
        action.payload.embargo_until,
        moment.ISO_8601
      );
      const featured_date = moment(
        action.payload.featured_date,
        moment.ISO_8601
      );
      const scheduledDate = moment(
        action.payload.scheduled_date,
        moment.ISO_8601
      );
      const lastUpdated = moment(action.payload.last_updated, moment.ISO_8601);
      const eta = moment(action.payload.eta, moment.ISO_8601);
      return {
        ...defaultCommonState,
        ...action.payload,
        embargo_until: embargo_until.isValid() ? embargo_until : null,
        featured_date: featured_date.isValid() ? featured_date : null,
        scheduled_date: scheduledDate.isValid() ? scheduledDate : null,
        last_updated: lastUpdated.isValid() ? lastUpdated : null,
        eta: eta.isValid() ? eta : null,
      };
    }

    case UPDATE_FORM_AT:
      return dotPropSet(state, action.payload.path, action.payload.value);

    case UPDATE_FORM:
    case GET_NEWSLETTER_SUCCEEDED:
      return {
        ...state,
        ...action.payload,
      };

    case APPEND_NEWSLETTER_CHUNK:
      return {
        ...state,
        chunks: [
          ...state.chunks,
          Object.assign(
            {},
            newsletterChunkInitialState,
            // We need to assign a temporary key to new newsletter chunks to prevent remount issues.
            {
              tempKey: Math.random()
                .toString(36)
                .substring(2, 7),
            }
          ),
        ],
      };

    case UPDATE_NEWSLETTER_CHUNK: {
      const chunks = [...state.chunks];
      chunks[action.payload.index] = {
        ...chunks[action.payload.index],
        ...action.payload.data,
      };
      return {
        ...state,
        chunks: chunks,
      };
    }

    case MOVE_NEWSLETTER_CHUNK_UP: {
      const chunks = [...state.chunks];
      chunks.splice(
        Math.max(action.payload - 1, 0),
        0,
        chunks.splice(action.payload, 1)[0]
      );

      return {
        ...state,
        chunks: chunks,
      };
    }

    case MOVE_NEWSLETTER_CHUNK_DOWN: {
      const chunks = [...state.chunks];
      chunks.splice(
        Math.min(action.payload + 1, chunks.length),
        0,
        chunks.splice(action.payload, 1)[0]
      );

      return {
        ...state,
        chunks: chunks,
      };
    }

    case DELETE_NEWSLETTER_CHUNK: {
      const chunks = [...state.chunks];
      chunks.splice(action.payload, 1);
      return {
        ...state,
        chunks: chunks,
      };
    }

    case VALIDATE_FORM_FAILED: {
      return { ...state, errors: action.payload };
    }

    case VALIDATE_FORM_SUCCEEDED: {
      return { ...state, errors: {} };
    }

    default:
      return state;
  }
}
